import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, X, Loader2, AlertCircle, Camera, Sparkles, ZoomIn } from 'lucide-react';
import { Layout } from '../components/Layout';
import { Lightbox } from '../components/Lightbox';
import { api } from '../lib/api';
import { prepareImage, type ImageMeta } from '../lib/image';

type ExerciseType = { id: number; code: string; name: string; description: string | null; is_active: boolean; sort_order: number };
type ModelConfig = { id: number; name: string; is_active: boolean; is_default: boolean };

type SelectedImage = {
  id: string;
  file: File;
  preview: string;
  meta: ImageMeta;
  preparing: boolean;
};

export function HomePage() {
  const navigate = useNavigate();
  const [metaLoading, setMetaLoading] = useState(true);
  const [types, setTypes] = useState<ExerciseType[]>([]);
  const [typeId, setTypeId] = useState<number | null>(null);
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [modelId, setModelId] = useState<number | null>(null);
  const [advanced, setAdvanced] = useState(false);
  const [images, setImages] = useState<SelectedImage[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState<{ elapsed: number; phase: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [zoomUrl, setZoomUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const meta = await api<{ exercise_types: ExerciseType[]; models: ModelConfig[] }>(
        '/api/v1/meta',
      );
      if (cancelled) return;
      if (meta.ok) {
        const actives = meta.data.exercise_types.filter((t) => t.is_active);
        setTypes(actives);
        if (actives.length > 0) setTypeId(actives[0].id);
        const activeModels = meta.data.models.filter((m) => m.is_active);
        setModels(activeModels);
        const def = activeModels.find((m) => m.is_default) ?? activeModels[0];
        if (def) setModelId(def.id);
      } else {
        setError(meta.error.message);
      }
      setMetaLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onPick = async (files: FileList | null) => {
    if (!files) return;
    const arr = Array.from(files);
    const remain = 5 - images.length;
    if (arr.length > remain) {
      setError(`一次最多 5 张,还可添加 ${remain} 张`);
    }
    const toAdd = arr.slice(0, remain);

    for (const f of toAdd) {
      const id = crypto.randomUUID();
      setImages((prev) => [
        ...prev,
        {
          id,
          file: f,
          preview: URL.createObjectURL(f),
          meta: { width: 0, height: 0, original_name: f.name, original_size: f.size, compressed: false },
          preparing: true,
        },
      ]);
      try {
        const { file, meta } = await prepareImage(f);
        setImages((prev) =>
          prev.map((p) => (p.id === id ? { ...p, file, meta, preparing: false } : p)),
        );
      } catch (e) {
        setImages((prev) => prev.filter((p) => p.id !== id));
        setError(`图片处理失败: ${e instanceof Error ? e.message : '未知错误'}`);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (id: string) => {
    setImages((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.preview);
      return prev.filter((p) => p.id !== id);
    });
  };

  const submit = async () => {
    if (submitting) return;
    setError(null);
    if (!typeId) {
      setError('请选择题型');
      return;
    }
    if (images.length === 0) {
      setError('请上传至少一张图片');
      return;
    }
    if (images.some((i) => i.preparing)) {
      setError('图片还在压缩,请稍候');
      return;
    }
    setSubmitting(true);
    setProgress({ elapsed: 0, phase: '上传中' });
    const startedAt = Date.now();
    const tick = setInterval(() => {
      setProgress((p) =>
        p ? { ...p, elapsed: Math.floor((Date.now() - startedAt) / 1000) } : p,
      );
    }, 1000);

    const fd = new FormData();
    fd.set('exercise_type_id', String(typeId));
    if (modelId) fd.set('model_config_id', String(modelId));
    const metaArr: ImageMeta[] = [];
    for (const img of images) {
      fd.append('images', img.file, img.file.name || 'img.jpg');
      metaArr.push(img.meta);
    }
    fd.set('image_meta', JSON.stringify(metaArr));

    try {
      const r = await api<{ recognition_id: number; status: string }>('/api/v1/recognize', {
        method: 'POST',
        body: fd,
      });
      clearInterval(tick);
      setSubmitting(false);
      setProgress(null);
      if (!r.ok) {
        setError(r.error.message || '上传失败,请稍后重试');
        return;
      }
      navigate(`/result/${r.data.recognition_id}`);
    } catch (e) {
      clearInterval(tick);
      setSubmitting(false);
      setProgress(null);
      const msg = e instanceof Error ? e.message : '未知原因';
      console.warn('[recognize] upload failed', msg);
      setError('网络中断了,请检查网络后再点一次"开始识别"');
    }
  };

  const modelsConfiguredButEmpty = models.length === 0;
  const noTypes = types.length === 0;

  return (
    <Layout>
      {metaLoading ? (
        <div className="flex items-center justify-center gap-2 py-12 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>加载中…</span>
        </div>
      ) : noTypes || modelsConfiguredButEmpty ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
            <div>
              <p className="font-medium">
                {noTypes ? '暂无可用题型' : '暂无可用模型'}
              </p>
              <p className="mt-1 text-sm">请联系管理员在后台配置后再来。</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <section className="rounded-xl border border-sky-100 bg-gradient-to-br from-sky-50 to-white p-5">
            <div className="flex items-start gap-3">
              <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-sky-600 text-white shadow-sm">
                <Sparkles className="h-5 w-5" />
              </span>
              <div>
                <h1 className="text-lg font-semibold text-slate-900">开始批改</h1>
                <p className="mt-1 text-sm text-slate-600">
                  选择题型 → 拍照上传 → 自动识别。整个过程大约 30~120 秒。
                </p>
              </div>
            </div>
          </section>

          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-700">① 选择题型</label>
            <select
              value={typeId ?? ''}
              onChange={(e) => setTypeId(Number(e.target.value))}
              disabled={submitting}
              className="h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-slate-900 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500"
            >
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <button
              type="button"
              onClick={() => setAdvanced((v) => !v)}
              disabled={submitting}
              className="text-sm text-slate-500 hover:text-slate-700 disabled:cursor-not-allowed disabled:text-slate-300"
            >
              {advanced ? '收起高级' : '高级选项'}
            </button>
            {advanced && (
              <div className="mt-2 space-y-1">
                <label className="text-sm font-medium text-slate-700">模型</label>
                <select
                  value={modelId ?? ''}
                  onChange={(e) => setModelId(Number(e.target.value) || null)}
                  disabled={submitting}
                  className="h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-slate-900 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500"
                >
                  {models.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                      {m.is_default ? ' (默认)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-center justify-between text-sm font-medium text-slate-700">
              <span>② 上传题目照片</span>
              <span className="text-xs text-slate-400">{images.length} / 5 张</span>
            </label>
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
              {images.map((img) => (
                <div
                  key={img.id}
                  className="group relative aspect-square overflow-hidden rounded-md border border-slate-200 bg-slate-50"
                >
                  <button
                    type="button"
                    onClick={() => !img.preparing && setZoomUrl(img.preview)}
                    className="block h-full w-full"
                    aria-label="放大查看"
                    disabled={img.preparing}
                  >
                    <img src={img.preview} alt="" className="h-full w-full object-cover" />
                    {!img.preparing && (
                      <span className="pointer-events-none absolute inset-0 hidden items-center justify-center bg-slate-900/30 text-white group-hover:flex">
                        <ZoomIn className="h-6 w-6" />
                      </span>
                    )}
                  </button>
                  {img.preparing && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/70">
                      <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
                    </div>
                  )}
                  {!submitting && (
                    <button
                      type="button"
                      onClick={() => removeImage(img.id)}
                      className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-900/60 text-white hover:bg-slate-900/80"
                      aria-label="删除"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
              {images.length < 5 && !submitting && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex aspect-square flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed border-slate-300 text-slate-400 transition-colors hover:border-sky-500 hover:bg-sky-50/50 hover:text-sky-600"
                >
                  {images.length === 0 ? (
                    <>
                      <Camera className="h-6 w-6" />
                      <span className="text-xs">拍照 / 选图</span>
                    </>
                  ) : (
                    <>
                      <Upload className="h-5 w-5" />
                      <span className="text-xs">再加一张</span>
                    </>
                  )}
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => onPick(e.target.files)}
            />
            <p className="text-xs text-slate-500">
              {submitting
                ? '识别已开始,照片已锁定。如需换图,请等识别完成或刷新本页。'
                : '支持常见手机照片格式,系统会自动压缩。点图可放大查看。'}
            </p>
          </div>

          {(() => {
            const lowRes = images.find(
              (i) => !i.preparing && i.meta.width > 0 && Math.max(i.meta.width, i.meta.height) < 1500,
            );
            if (!lowRes) return null;
            return (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                有照片长边不足 1500 像素,手写笔画细节可能不够清晰,建议在光线好的环境里重新拍摄整页。
              </div>
            );
          })()}

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={submit}
            disabled={submitting || images.length === 0}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-sky-600 text-base font-semibold text-white shadow-sm transition-colors hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting && <Loader2 className="h-5 w-5 animate-spin" />}
            {submitting
              ? progress
                ? `${progress.phase}(${progress.elapsed}s)`
                : '识别中…'
              : '③ 开始识别'}
          </button>
          {submitting && (
            <p className="text-center text-xs text-slate-500">
              AI 通常需要 30~120 秒。即便现在关掉页面,识别也会在后台继续,稍后可在"历史记录"查看。
            </p>
          )}
        </div>
      )}
      {zoomUrl && <Lightbox src={zoomUrl} onClose={() => setZoomUrl(null)} />}
    </Layout>
  );
}
